import {Injectable} from '@angular/core';
import {RequesterService} from "../shared/services/requester/requester.service";
import {UserMapper} from "./user-mapper";
import {DbResponse} from "../shared/interceptors/db-response";
import {User} from "./user";
import {BehaviorSubject} from "rxjs";
import {EmailPreferencesMapper} from '../account/email-preferences/email-preferences-mapper';
import {HttpClient, HttpEvent, HttpHeaders, HttpRequest} from '@angular/common/http';
import {CookieService} from "ngx-cookie";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import * as fromStore from "../store";
import {Store} from '@ngrx/store';
import {clientRoles} from "./user-roles-actions";
import {GlobalEventDispatcherService} from "../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {
  NotificationDialogComponent
} from "../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {TextUtils} from "../shared/utils/text-utils";
import {DialogService} from "../../ng2-bootstrap-modal/dialog.service";
import {filter, map, switchMap} from "rxjs/operators";
import {Observable} from "rxjs/internal/Observable";

export interface StateInterface {
  id: number;
  name: string;
  iso2: string;
}

@Injectable()
export class UserService {

  private _user$: BehaviorSubject<DbResponse>;
  private currentWorkspaceId: any;
  private ownerWorkspaceId: any;
  private currentWorkspaceRoleId: any;

  constructor(
    private _requester: RequesterService,
    private _cookieService: CookieService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _dialogService: DialogService,
    private http: HttpClient,
    private _store: Store<fromStore.DbStoreState>
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe(workspaceIdEntities => {
      this.setWorkspaceEntities(workspaceIdEntities.ownerWorkspaceId, workspaceIdEntities.currentWorkspaceId, workspaceIdEntities.currentWorkspaceRoleId);
    });
    this._user$ = new BehaviorSubject<DbResponse>(null);
  }

  // TODO remove
  public getIpAddress() {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      "https://ipinfo.io/"
    );
    return this.http.request<DbResponse>(httpRequest);
  }

  private headers = {
    headers: new HttpHeaders({
        'X-CSCAPI-KEY': 'RXdMaW14YUxrT1hyMkRpSmRBc0JONmhzMGswYTdwUWJhSFZoTHdVUw==',
      }
    )
  };

  public getStatesByCountryCode(code: string): Observable<HttpEvent<StateInterface[]>> {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      `https://api.countrystatecity.in/v1/countries/${code}/states`,
      this.headers
    );
    return this.http.request<StateInterface[]>(httpRequest);
  }

  public getCitiesByStateCounties(CountryCode: string, cityCode: string): Observable<HttpEvent<{
    name: string;
    id: number
  }[]>> {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      `https://api.countrystatecity.in/v1/countries/${CountryCode}/states/${cityCode}/cities`,
      this.headers
    );
    return this.http.request<{ name: string, id: number }[]>(httpRequest);
  }

  public getCounties(): Observable<HttpEvent<StateInterface[]>> {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      `https://api.countrystatecity.in/v1/countries`,
      this.headers
    );
    return this.http.request<StateInterface[]>(httpRequest);
  }

  public searchKnowledgeArticles(query: string | null) {
    const searchByFilter = query ? "/search.json?query=" + query : "";
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      "https://help.10web.io/api/v2/help_center/articles" + searchByFilter
    );
    return this.http.request<DbResponse>(httpRequest);
  }

  public getKnowledgeArticle(articleId: number | null) {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      "https://help.10web.io/api/v2/help_center/en-us/articles/" + articleId
    );
    return this.http.request<DbResponse>(httpRequest);
  }

  public getKnowledgeArticlesBySectionId(sectionId: number|null) {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      "https://help.10web.io/api/v2/help_center/en-us/sections/" + sectionId + "/articles?per_page=300"
    );
    return this.http.request<DbResponse>(httpRequest);
  }

  public getKnowledgeArticlesBySlug(labels: string|null, url: string = '') {
    const requestUrl = url ? url : "https://help.10web.io/api/v2/help_center/articles/search.json?label_names=" + labels + "&per_page=300";
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      requestUrl
    );
    return this.http.request<DbResponse>(httpRequest);
  }

  public getKnowledgeSectionsByCategoryId(categorId: number|null) {
    const httpRequest: HttpRequest<any> = new HttpRequest(
      "GET",
      "https://help.10web.io/api/v2/help_center/en-us/categories/" + categorId + "/sections"
    );
    return this.http.request<DbResponse>(httpRequest);
  }

  /*public getTrustPilotReviews() {
    return this.http.jsonp('https://www.trustpilot.com/review/10web.io', 'callback')
      .subscribe(
        (responseData: any): void => {
          const dbResponse: DbResponse = new DbResponse();
          dbResponse.success = true;
          dbResponse.data = responseData;
        },
        (errorResponse): void => {
          if (errorResponse instanceof HttpErrorResponse) {
            DbResponse.create(errorResponse);
          }
          else {
            console.error(errorResponse && errorResponse.message);
            DbResponse.create(errorResponse);
          }
        }
      );
  }*/

  public getUser(): BehaviorSubject<DbResponse> {
    const userAccessTokenCookie = this._cookieService.get("dbTenWebDeveloperAccess");
    const currentWorkspaceId = this._cookieService.get("dbTenWebDeveloperAccessCurrentWID");
    const ownerWorkspaceId = this._cookieService.get("dbTenWebDeveloperAccessWID");
    const currentWorkspaceRoleId = this._cookieService.get("dbTenWebDeveloperAccessCurrentWRoleID");
    if (userAccessTokenCookie) {
      localStorage.removeItem('dbUserAccessToken');
      localStorage.removeItem('dbCurrentWorkspaceId');
      localStorage.removeItem('dbOwnWorkspaceId');
      localStorage.removeItem('dbCurrentWorkspaceRoleId');
      localStorage.removeItem('dbUserRefreshToken');
      localStorage.removeItem('dbFirebaseToken');
      localStorage.setItem('dbUserAccessToken', userAccessTokenCookie);
      localStorage.setItem('dbCurrentWorkspaceId', currentWorkspaceId.toString());
      localStorage.setItem('dbOwnWorkspaceId', ownerWorkspaceId.toString());
      localStorage.setItem('dbCurrentWorkspaceRoleId', currentWorkspaceRoleId.toString());
      localStorage.setItem('dbDeveloper', '1');
      this.setWorkspaceEntities(ownerWorkspaceId, currentWorkspaceId, currentWorkspaceRoleId);

      this._store.dispatch(new fromStore.SetWorkspaceIdEntities(
        {
          ownerWorkspaceId: +ownerWorkspaceId,
          currentWorkspaceId: +currentWorkspaceId,
          currentWorkspaceRoleId: +currentWorkspaceRoleId,
          globalEventDispatcherService: this._globalEventDispatcher
        }
      ));
      this._cookieService.remove("dbTenWebDeveloperAccess", {path: '/', domain: '10web.io'});
      this._cookieService.remove("dbTenWebDeveloperAccessCurrentWID", {path: '/', domain: '10web.io'});
      this._cookieService.remove("dbTenWebDeveloperAccessWID", {path: '/', domain: '10web.io'});
      this._cookieService.remove("dbTenWebDeveloperAccessCurrentWRoleID", {path: '/', domain: '10web.io'});
    }
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    if (!userAccessToken) {
      const response: DbResponse = new DbResponse();
      response.success = true;
      response.data = null;

      return new BehaviorSubject<DbResponse>(response);
    } else {
      return this._requester.auth.get(`workspaces/${this.ownerWorkspaceId}/user/me`, null, true);
    }
  }

  public updateUserData(userData: User, sendToAnalytics = false, productId = null): BehaviorSubject<DbResponse> {
    if (sendToAnalytics) {
      // STALE: predates reseller cid support (see ResellerCidService / auth.service.ts).
      // Currently unreachable — both callers omit sendToAnalytics, so it stays false —
      // which is why this hasn't caused visible harm. This block only knows about
      // tenweb_ga_user and can never emit isBlocked: 0, unlike auth.service.ts's
      // resellerCid-aware logic. Update it to match auth.service.ts before ever
      // enabling sendToAnalytics here, or a post-signup PATCH through this path
      // could overwrite a correctly-persisted `isBlocked: 0` with `''&isBlocked=3`.
      let isBlocked: number;
      const tenWebGaUser = this._cookieService.get("tenweb_ga_user") && this._cookieService.get("tenweb_ga_user").indexOf('Tenweb_') === 0 && this._cookieService.get("tenweb_ga_user");
      const _cid = tenWebGaUser || '';
      isBlocked = tenWebGaUser ? 1 : 3;
      userData = Object.assign({}, userData, {
        ga_client_cookie: _cid + `&isBlocked=${isBlocked}`
      });
      // }
    }

    const data = UserMapper.convertToApiFormat(userData, productId);
    /* to do
        * add workspaces/${this.ownerWorkspaceId}/ to route
        *   */
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .patch(`workspaces/${this.ownerWorkspaceId}/user/me`, null, data, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public contactUs(formData: any): BehaviorSubject<DbResponse> {
    const data = UserMapper.contactUsConvertToApiFormat(formData);

    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/contact-us`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {
        }

        return response;
      })));
  }

  public getEmailPreferences(userToken: string): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.ownerWorkspaceId}/preferences/${userToken}`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = EmailPreferencesMapper.map(response.data);
        }
        return response;
      })));
  }

  public updateEmailPreferences(userToken: string, preferences: any): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
        .post(`workspaces/${this.ownerWorkspaceId}/unsubscribe/${userToken}`, null, EmailPreferencesMapper.unmap(preferences))
    );
  }

  public createZendeskTicket(data: any): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/me/zendesk_ticket`, null, data));
  }

  public getZendeskLink(type = 'help-center'): BehaviorSubject<DbResponse> {
    /* to do
    * add workspaces/${this.ownerWorkspaceId}/ to route
    *   */
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.ownerWorkspaceId}/user/me/zendesk-link/${type}`));
  }

  public getManagerLoginToken(siteUrl: string, nonce: string, isNewConnectionFlow: boolean, fromPlugin: string): BehaviorSubject<DbResponse> {
    let params = `site_url=${siteUrl}&nonce=${nonce}&from_plugin=${fromPlugin}`;
    if (isNewConnectionFlow) {
      params += '&new_connection_flow=1';
    }
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/domains/login-token?${params}`));
  }

  public showUpdateBoosterDialog(returnUrl: string): void {
    if (!this._dialogService.hasOpenedDialog()) {
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: TextUtils.getText('text_boosterLatestVersionRequired'),
        message: TextUtils.getText('text_boosterLatestVersionRequiredDesc'),
        buttonText: TextUtils.getText('text_update'),
        width: 470,
        action: 'showUpdateBooster',
        buttonAlign: 'right'
      }).subscribe((confirmed: string): void => {
        if (confirmed && returnUrl) {
          window.location.href = returnUrl.replace('options-general.php?page=two_settings_page', 'plugins.php');
        }
      });
    }
  }

  public getQrCode(): BehaviorSubject<DbResponse> {
    /* to do
    * add workspaces/${this.ownerWorkspaceId}/ to route
    *   */
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.ownerWorkspaceId}/user/me/2fa/setup`));
  }

  public finish2faSetup(_2fa): BehaviorSubject<DbResponse> {
    const data = {
      '2fa_token': _2fa
    };
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/2fa/finish-setup`, null, data);
  }


  public disable2fa(_2fa, recovery: boolean = false): BehaviorSubject<DbResponse> {
    let data: {};
    if (recovery) {
      data = {
        '2fa_recovery_token': _2fa
      };
    } else {
      data = {
        '2fa_token': _2fa
      };
    }
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/2fa/disable`, null, data);
  }

  public checkUser2fa(data): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`2fa/status`, null, data);
  }

  public setUserGrantAccess(data): BehaviorSubject<DbResponse> {
    const _data = {
      allow_access: data ? 1 : 0
    };
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/allow-grant-access`, null, _data);
  }

  public setNpsRating(data): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/me/nps`, null, data);
  }

  public sendMagicLinkEmailPublic(email, path) {
    const data = {email, path};
    return this._requester.core
      .post(`send-magic-link`, null, data);
  }

  public sendSource() {
    const data = {
      source: "checkout"
    };
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/set-source`, null, data);
  }

  public setToLocal(data): Observable<DbResponse> {
    let currentWorkspaceId = this.currentWorkspaceId;
    if ([clientRoles.SITE_DEV, clientRoles.SUITE_DEV].includes(this.currentWorkspaceId)) {
      currentWorkspaceId = this.ownerWorkspaceId;
    }

    return this.getFromLocal().pipe(
      filter((response: DbResponse) => !!response),
      switchMap((response: DbResponse) => {
        const existingData = response.data || {};
        const mergedData = { ...existingData, ...data };
        const requestData = { data: mergedData };

        return this._requester.core.post(
          `workspaces/${currentWorkspaceId}/user/client-data`,
          null,
          requestData
        );
      })
    );
  }


  public deleteExpiration(): BehaviorSubject<DbResponse> {
    let currentWorkspaceId = this.currentWorkspaceId;
    if ([clientRoles.SITE_DEV].includes(this.currentWorkspaceRoleId)) {
      currentWorkspaceId = this.ownerWorkspaceId;
    }
    return this._requester.core
      .post(`workspaces/${currentWorkspaceId}/user/delete-expiration`, null, null);
  }

  public getFromLocal(): BehaviorSubject<DbResponse> {
    return this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/user/client-data`, null, true);
  }

  public sendTypeFormAnswerId(responseId: string, formId: string): BehaviorSubject<DbResponse> {
    const data = {
      form_id: formId,
      included_response_id: responseId
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/type_form`, null, data);
  }

  private setWorkspaceEntities(ownerWorkspaceId, currentWorkspaceId, currentWorkspaceRoleId): void {
    this.ownerWorkspaceId = ownerWorkspaceId;
    this.currentWorkspaceId = currentWorkspaceId || ownerWorkspaceId;
    this.currentWorkspaceRoleId = currentWorkspaceRoleId;
  }

  public sendEmailForSlackInvitation(email: string, fromPage: string, meta: string): BehaviorSubject<DbResponse> {
    const data = {
      slack_email: email,
      from_page: fromPage,
      meta: meta
    };
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/slack-connect`, null, data);
  }

  public enableProfessionalFeatures(requestText: string): BehaviorSubject<DbResponse> {
    const requestBody = {
      message: requestText
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/professional-requests `, null, requestBody);
  }

  public switchToAnnual(): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/upgrade-period`, null);
  }

  public getCampaignInfo() {
    const storedValue = localStorage.getItem('dbDemoCampaignInfo');

    const couponDays = storedValue?.match(/ct(\d+)/)?.[1] || null;
    const couponPercent = storedValue?.match(/cd(\d+)/)?.[1] || null;

    return (couponDays || couponPercent ?  { couponDays, couponPercent } : null);
  }

  public getCompanyInfo(): BehaviorSubject<DbResponse> {
    return this._requester.core
      .get(`agency/information`);
  }
}
